import { PublicClientApplication } from "@azure/msal-browser";
import { FlightCardTypes } from "../../enum/flights/FlightEnum";
import { IGlobalDateFilter } from "../../models/filters/IFilters";
import IFlightSummary, {
  IFlightSummaryKpi,
} from "../../models/flights/IFlightSummary";
import FlightSummaryService from "../../services/flight-summary/flightsummary.service";
import { APIHelper } from "../api/APIHelper";
import { CommonHelper } from "../common/CommonHelper";
import { FLIGHT_COLORS, FLIGHT_COLORS_DONUT } from "../common/Constants";

export abstract class FlightSummaryHelper {
  public static async getFlightSummary(
    msalInstance: PublicClientApplication,
    airportCode: string,
    globalFilterDate: IGlobalDateFilter,
    timezone: string
  ): Promise<any> {
    const flightService = new FlightSummaryService();
    const flightSummaryMethod = (accessToken: string) => {
      return flightService.getFlightSummary(
        accessToken,
        airportCode,
        globalFilterDate,
        timezone
      );
    };

    return APIHelper.CallAPI(msalInstance, null, flightSummaryMethod);
  }

  public static getTotalFlightCount(data: IFlightSummary[], key: string) {
    return data.reduce((a, b) => a + (b as { [k in string]: any })[key], 0);
  }

  public static getChartData(data: IFlightSummary[], key: string) {
    const chartData: any[] = [];
    const total = this.getTotalFlightCount(data, key);
    data?.forEach((item: any, index: number) => {
      const obj = {
        resourceName: CommonHelper.toTitleCase(key),
        resourceCount: (item as { [k in string]: any })[key],
        resourceTitle: CommonHelper.toTitleCase(item.type),
        resourceColor: (FLIGHT_COLORS as { [k in string]: any })[key][index],
        resourceTotal: total,
      };
      chartData.push(obj);
    });
    return chartData;
  }

  public static getDonutChartData(data: any) {
    const chartData: any[] = [];
    data?.breakdown?.forEach((item: any) => {
      const obj = {
        resourceName: "Total",
        resourceCount: item.kpiValue,
        resourceTitle: CommonHelper.toTitleCase(item.type),
        resourceColor: (FLIGHT_COLORS_DONUT as { [k in string]: any })[
          item.type
        ],
        resourceTotal: data.kpiValue,
      };
      chartData.push(obj);
    });

    // Rearrange chartData based on item.type
    chartData.sort((a, b) => b.resourceTitle.localeCompare(a.resourceTitle));
    return chartData;
  }
  public static getFlightTotalSummary(data: IFlightSummary[]) {
    return {
      total: {
        count: FlightSummaryHelper.getTotalFlightCount(data, "total"),
        color: "fc--darkprimary",
      },
      delayed: {
        count: FlightSummaryHelper.getTotalFlightCount(data, "delayed"),
        color: "fc--cherry",
      },
      cancelled: {
        count: FlightSummaryHelper.getTotalFlightCount(data, "cancelled"),
        color: "fc--brown",
      },
    };
  }

  public static getFlightKpiTotalSummary(data: IFlightSummaryKpi[]) {
    const overall = data.find((item) => item.type.toLowerCase() === "overall");
    const getBreakdownValue = (type: string) =>
      Number(
        overall?.breakdown.find(
          (item) => item.type.toLowerCase() === type.toLowerCase()
        )?.kpiValue
      ) || 0;

    return {
      total: {
        count: Number(overall?.kpiValue) || 0,
        color: "fc--darkprimary",
      },
      delayed: {
        count: getBreakdownValue("Delayed"),
        color: "fc--cherry",
      },
      cancelled: {
        count: getBreakdownValue("Cancelled"),
        color: "fc--brown",
      },
    };
  }

  public static getResourceTitle(key: string) {
    if (key === "Total") {
      return key + " Flights";
    } else if (key === "Delayed") {
      return key + " (In-time)";
    } else {
      return key;
    }
  }

  public static getClassName(isDetailsPage: boolean, key: string) {
    if (key === "total") {
      return isDetailsPage
        ? "ms-md4 ms-xxl3 divider-v-grey divider-v-grey--hiddenXlDown"
        : "ms-md4";
    } else if (key === "delayed") {
      return isDetailsPage ? "ms-md4 ms-xxl3" : "ms-md4 divider-v-grey";
    } else if (key === "cancelled") {
      return isDetailsPage ? "ms-md4 ms-xxl3" : "ms-md4 divider-v-grey";
    }
    return "";
  }

  public static getBufferValues(hopSettingsDataList: any, kpiType: string) {
    const kpiSettings = hopSettingsDataList?.find(
      (item: any) => item.kpiType === kpiType
    )?.data;
    if (!kpiSettings || !Array.isArray(kpiSettings)) {
      return {};
    }
    return {
      arrivalLateBuffer:
        kpiSettings.find((item: any) => item.type === "arrivals")?.lateBuffer ??
        "",
      arrivalEarlyBuffer:
        kpiSettings.find((item: any) => item.type === "arrivals")
          ?.earlyBuffer ?? "",
      departureLateBuffer:
        kpiSettings.find((item: any) => item.type === "departures")
          ?.lateBuffer ?? "",
      departureEarlyBuffer:
        kpiSettings.find((item: any) => item.type === "departures")
          ?.earlyBuffer ?? "",
      overallBuffer:
        kpiSettings.find((item: any) => item.type === "all")?.lateBuffer ?? "",
    };
  }
  public static getBufferTexts(hopSettingsDataList: any) {
    const onTimePerformanceBuffers = FlightSummaryHelper.getBufferValues(
      hopSettingsDataList,
      "onTimePerformance"
    );
    const turnaroundSufficiencyBuffers = FlightSummaryHelper.getBufferValues(
      hopSettingsDataList,
      "turnaroundSufficiency"
    );
    const slotAdherenceBuffers = FlightSummaryHelper.getBufferValues(
      hopSettingsDataList,
      "slotAdherence"
    );
    return {
      onTimePerformanceBuffers: onTimePerformanceBuffers,
      turnaroundSufficiencyBuffers: turnaroundSufficiencyBuffers,
      slotAdherenceBuffers: slotAdherenceBuffers,
    };
  }

  public static getCardBuffer(card: FlightCardTypes) {
    switch (card) {
      case FlightCardTypes.OTP:
        return "onTimePerformanceBuffers";
      case FlightCardTypes.TURNAROUND_SUFFICIENCY:
        return "turnaroundSufficiencyBuffers";
      case FlightCardTypes.SLOT_ADHERENCE:
        return "slotAdherenceBuffers";
      default:
        return "";
    }
  }
  public static getBufferValue(hopSettingsData: any, kpiType: string) {
    return {
      arrivalLateBuffer: Array.isArray(hopSettingsData)
        ? (hopSettingsData.find((item: any) => item.type === "arrivals")
            ?.lateBuffer ?? "")
        : "",
      arrivalEarlyBuffer: Array.isArray(hopSettingsData)
        ? (hopSettingsData.find((item: any) => item.type === "arrivals")
            ?.earlyBuffer ?? "")
        : "",
      departureLateBuffer: Array.isArray(hopSettingsData)
        ? (hopSettingsData.find((item: any) => item.type === "departures")
            ?.lateBuffer ?? "")
        : "",
      departureEarlyBuffer: Array.isArray(hopSettingsData)
        ? (hopSettingsData.find((item: any) => item.type === "departures")
            ?.earlyBuffer ?? "")
        : "",
      overallBuffer: Array.isArray(hopSettingsData)
        ? (hopSettingsData.find((item: any) => item.type === "all")
            ?.lateBuffer ?? "")
        : "",
    };
  }
}
